import { apexCookieDomain } from "@/lib/cookies/apex-domain";
import { deleteClientCookie, setClientCookie } from "@/lib/cookies/client";
import { readConsentFromCookieHeader } from "./server";
import {
	ALL_GRANTED,
	CONSENT_COOKIE_NAME,
	CONSENT_EVENT,
	CONSENT_STORAGE_KEY,
	CONSENT_VERSION,
	type ConsentChoices,
	type ConsentRecord,
	ONLY_NECESSARY,
} from "./types";

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/**
 * Personalization cookies gated on the "preferences" consent category (see
 * `@/lib/consent/region-context`). Deleted whenever a consent write leaves
 * "preferences" un-granted — including the very first reject/save, so a
 * visitor who never gets a chance to have one written is unaffected, and one
 * who withdraws consent has it cleared immediately.
 */
const PREFERENCE_GATED_COOKIES = [
	"ld_pref_v1",
	"ld_filters_v1",
	"hunt_interest_v1",
	"ld_recent_jobs_v1",
	"ld_seen_v1",
] as const;

export function readConsent(): ConsentRecord | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
		if (raw) {
			const parsed = JSON.parse(raw) as ConsentRecord;
			if (
				parsed &&
				typeof parsed === "object" &&
				parsed.version === CONSENT_VERSION &&
				parsed.choices &&
				parsed.choices.necessary === true
			) {
				return parsed;
			}
		}
	} catch {
		// fall through to the cookie fallback below
	}
	// localStorage is per-origin, so a choice made on one zone (e.g. the apex)
	// never shows up here on another (e.g. app.lokerdollar.com). The consent
	// cookie IS shared across the zones (see `consentCookieDomain()` in
	// `writeConsent()`), so fall back to it — same parsing/validation the
	// server side already uses in `readConsentFromCookieHeader()`.
	try {
		return readConsentFromCookieHeader(document.cookie);
	} catch {
		return null;
	}
}

export function writeConsent(choices: ConsentChoices): ConsentRecord {
	const record: ConsentRecord = {
		version: CONSENT_VERSION,
		choices: { ...choices, necessary: true },
		updatedAt: new Date().toISOString(),
	};
	if (typeof window !== "undefined") {
		try {
			window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
		} catch {
			// localStorage unavailable (private mode quota) — fall through to cookie
		}
		try {
			setClientCookie(CONSENT_COOKIE_NAME, JSON.stringify(record), {
				maxAgeSeconds: ONE_YEAR_SECONDS,
				domain: apexCookieDomain(),
			});
		} catch {
			// document.cookie may throw in sandboxed contexts
		}
		// A write that leaves "preferences" un-granted must not leave a stale
		// personalization cookie behind — covers both an explicit withdrawal
		// (previously granted, now rejected) and the ordinary first-visit
		// reject/save where nothing was written yet (no-op delete, harmless).
		if (!record.choices.preferences) {
			for (const name of PREFERENCE_GATED_COOKIES) {
				try {
					deleteClientCookie(name);
				} catch {
					// ignore
				}
			}
		}
		try {
			window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record }));
		} catch {
			// CustomEvent unavailable in some test environments
		}
	}
	return record;
}

export function clearConsent(): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.removeItem(CONSENT_STORAGE_KEY);
	} catch {
		// ignore
	}
	try {
		deleteClientCookie(CONSENT_COOKIE_NAME);
	} catch {
		// ignore
	}
	try {
		window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
	} catch {
		// ignore
	}
}

export function acceptAll(): ConsentRecord {
	return writeConsent(ALL_GRANTED);
}

export function rejectAll(): ConsentRecord {
	return writeConsent(ONLY_NECESSARY);
}
